import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/SEOMetadata';
import ProductFAQ from '@/components/landing/ProductFAQ';
import React, { Suspense } from 'react';
import Header from '@/components/landing/product/sub/Header'
import databg from '@/assets/products/corp.png';
import { Database, Zap, Globe, Clock, TrendingDown, Activity } from 'lucide-react';
import StyledProducts from '../styles/ProductsPageStyles';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

import Marquee from '@/components/landing/product/sub/Logos';
import FeaturesGrid from '@/components/landing/product/sub/FeaturesGrid';

const loadGlobeComp = () => import('@/components/landing/product/sub/Globe');
const GlobeComp = React.lazy(loadGlobeComp);
const loadDataGrid = () => import('@/components/landing/product/DataGrid');
const DataGrid = React.lazy(loadDataGrid);
const loadCardList = () => import('@/components/landing/product/CardList');
const CardList = React.lazy(loadCardList);
const loadResponses = () => import('@/components/landing/product/sub/Responses');
const Responses = React.lazy(loadResponses);
const loadDatasetDetails = () => import('@/components/landing/product/main/DatasetDetails');
const DatasetDetails = React.lazy(loadDatasetDetails);
const loadW1 = () => import('@/components/landing/product/main/datasets/corporate-intelligence');
const W1 = React.lazy(loadW1);
const loadW2 = () => import('@/components/landing/product/main/datasets/corporate-signal-card');
const W2 = React.lazy(loadW2);
const loadW3 = () => import('@/components/landing/product/main/datasets/corporate-attention-card');
const W3 = React.lazy(loadW3);
const loadW4 = () => import('@/components/landing/product/main/datasets/corporate-vital-signs-card');
const W4 = React.lazy(loadW4);
const loadW5 = () => import('@/components/landing/product/main/datasets/corporate-dependency-map-card');
const W5 = React.lazy(loadW5);
const loadOutcomes = () => import('@/components/landing/product/sub/OutcomeStats');
const Outcomes = React.lazy(loadOutcomes);
const loadPricingTable = () => import('@/components/landing/PricingTable');
const PricingTable = React.lazy(loadPricingTable);
const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);


const StocksPage: React.FC = () => {
  useIdlePrefetch([loadGlobeComp, loadDataGrid, loadCardList, loadResponses, loadDatasetDetails, loadW1, loadW2, loadW3, loadW4, loadW5, loadOutcomes, loadPricingTable, loadGetStarted, loadFooter]);
  return (
    <>
      <SEOMetadata
        title="Company Profile API: Fundamentals & Financials"
        description="Company profile API with fundamentals, executive leadership, and financial data for 100,000+ public companies. Built for investment research and due diligence."
        canonicalUrl="https://axionquant.com/products/corporate-profiles"
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Axion Company Profile API",
          "description": "Company profile API with fundamentals, executive leadership, financial data, and business intelligence for 100,000+ public companies. Unify fundamental data, executive insights, digital footprint, and supply chain relationships.",
          "brand": { "@type": "Brand", "name": "AxionQuant" },
          "image": "https://axionquant.com/meta.jpg",
          "category": "Company Profile Data API",
          "offers": { "@type": "AggregateOffer", "lowPrice": "29", "highPrice": "99", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "description": "Free tier available; paid plans from $29/month" }
        })}</script>
      </Helmet>

      <StyledProducts>


      <Header
  docs="/docs/03-fundamentals/corp.-intelligence"
  title="The Company Profile API for Fundamentals & Financial Data"
  body="Unify company fundamentals, financial data, and business intelligence into a single API - executive insights, digital footprint, and supply chain relationships for any public company."
  crumb={{name: 'Corporate Profiles', path:'/products/corporate-profiles'}}
  tabs={[
    {
      label: 'Company Profile',
      language: 'python',
      filename: 'company_profile.py',
      bg: '10.png',
      code: `from axion import Axion

client = Axion(api_key="your_api_key")
ticker = "NVDA"

profile  = client.profiles.profile(ticker)
info     = client.profiles.info(ticker)
summary  = client.profiles.summary(ticker)
calendar = client.profiles.calendar(ticker)

sector   = profile["sector"]
industry = profile["industry"]
price    = summary["regularMarketPrice"]
high_52w = summary["fiftyTwoWeekHigh"]
earnings = calendar["earnings"]["earningsDate"]
`,
    },
    {
      label: 'Executive Intelligence',
      language: 'python',
      filename: 'executive_intelligence.py',
      bg: '16.png',
      code: `from axion import Axion

client = Axion(api_key="your_api_key")
ticker = "GOOGL"

ownership    = client.insiders.ownership(ticker)
institutions = client.insiders.institutions(ticker)
transactions = client.insiders.transactions(ticker)

insider_pct  = ownership["insidersPercentHeld"]
inst_float   = ownership["institutionsFloatPercentHeld"]

top_holders  = institutions[:5]
recent_trades = [
    {
        "filer":  tx["filerName"],
        "action": tx["transactionText"],
        "value":  tx["value"],
    }
    for tx in transactions[:4]
]
`,
    },
    {
      label: 'Financial Quality',
      language: 'python',
      filename: 'financial_quality.py',
      bg: '18.png',
      code: `from axion import Axion

client = Axion(api_key="your_api_key")
ticker = "MSFT"

revenue = client.financials.revenue(ticker, periods=4)
income  = client.financials.net_income(ticker, periods=4)
fcf     = client.financials.free_cash_flow(ticker, periods=4)
metrics = client.financials.metrics(ticker)

margins = [
    {
        "period": r["date"],
        "margin": n["value"] / r["value"],
        "fcf":    f["value"],
    }
    for r, n, f in zip(revenue, income, fcf)
]

roe          = metrics["returnOnEquity"]
gross_margin = metrics["grossMargin"]
debt_equity  = metrics["debtToEquity"]
`,
    },
    {
      label: 'Competitive Landscape',
      language: 'python',
      filename: 'competitive_landscape.py',
      bg: '17.png',
      code: `from axion import Axion

client = Axion(api_key="your_api_key")
ticker = "AAPL"

peers     = client.supply_chain.peers(ticker)
suppliers = client.supply_chain.suppliers(ticker)
customers = client.supply_chain.customers(ticker)

peer_stats = [
    {
        "ticker": p["ticker"],
        "pe":     client.profiles.statistics(p["ticker"])["trailingPE"],
        "beta":   client.profiles.statistics(p["ticker"])["beta"],
    }
    for p in peers[:5]
]

key_suppliers = [s["name"] for s in suppliers[:4]]
key_customers = [c["name"] for c in customers[:4]]
`,
    },
  ]}
/>

      <Marquee/>

      <FeaturesGrid
        title="Company Profile API. "
        subtitle="Company fundamentals, financial data, and business intelligence in one endpoint. "
        features={ [
        {
          fig: 'FIG 0.1',
          icon: <Zap size={80} color="var(--purple)" />,
          title: 'Executive Leadership',
            description: 'Complete bios, compensation details, and insider ownership for CEOs, CFOs, and senior management. Company intelligence for tracking leadership changes and incentive alignment.',
          },
          {
            fig: 'FIG 0.2',
            icon: <Clock size={80} color="var(--green)" />,
            title: 'Corporate Actions',
            description: 'Meticulously cleaned timeline of dividends, splits, spinoffs, M&A, and name changes. Critical for adjusting historical price series and capital allocation analysis.',
          },
          {
            fig: 'FIG 0.3',
            icon: <Database size={80} color="var(--red)" />,
            title: 'Business Profile',
            description: 'In-depth company information - descriptions, business segments, products, services, and industry classification. Understand revenue drivers and operational structure.',
          },
          {
            fig: 'FIG 0.4',
            icon: <TrendingDown size={80} color="var(--purple)" />,
            title: 'Competitive Landscape',
            description: 'Peer group mapping, market positioning, and competitive advantage analysis. Benchmark any company against its industry and direct rivals.',
          },
          {
            fig: 'FIG 0.5',
            icon: <Activity size={80} color="var(--green)" />,
            title: 'Digital Footprint',
            description: 'Daily and historical web traffic, app download estimates, social media sentiment, and search trends. Quantify brand momentum with non-financial growth signals.',
          },
          {
            fig: 'FIG 0.6',
            icon: <Globe size={80} color="var(--red)" />,
            title: 'Global Coverage',
            description: '100,000+ public companies across developed and emerging markets. Company fundamentals API with 200+ data points per entity, updated daily.',
          },
        ]}/>



      <Suspense fallback={null}>
      <GlobeComp
        eyebrow="Company Intelligence API"
        headingPlain="Company profiles"
        headingEm="for 100,000+ companies."
        body="Company intelligence API for global public companies - fundamentals, financial data, and business intelligence, normalized, enriched, and ready for analysis."
        stats={[
          { v: "100K+",  l: "Companies"   },
          { v: "500K+",  l: "Executives"   },
          { v: "Daily",  l: "Updated"      },
          { v: "200+",   l: "Data Points"  },
        ]}
        markers={[
          { id: "ny",        location: [40.71, -74.01],   label: "New York" },
          { id: "ldn",       location: [51.51, -0.09],    label: "London" },
          { id: "tyo",       location: [35.68, 139.77],   label: "Tokyo" },
          { id: "hk",        location: [22.28, 114.16],   label: "Hong Kong" },
          { id: "syd",       location: [-33.87, 151.21],  label: "Sydney" },

          { id: "sf",        location: [37.77, -122.42],  label: "San Francisco Tech Hub" },
          { id: "sea",       location: [47.61, -122.33],  label: "Seattle Corporate Hub" },
          { id: "la",        location: [34.05, -118.24],  label: "Los Angeles" },
          { id: "chi",       location: [41.88, -87.63],   label: "Chicago" },
          { id: "bos",       location: [42.36, -71.06],   label: "Boston" },
          { id: "tor",       location: [43.65, -79.38],   label: "Toronto" },
          { id: "van",       location: [49.28, -123.12],  label: "Vancouver" },
          { id: "hou",       location: [29.76, -95.37],   label: "Houston Energy Hub" },
          { id: "atl",       location: [33.75, -84.39],   label: "Atlanta" },
          { id: "dallas",    location: [32.78, -96.80],   label: "Dallas" },
          { id: "austin",    location: [30.27, -97.74],   label: "Austin" },
          { id: "miami",     location: [25.76, -80.19],   label: "Miami" },

          { id: "paris",     location: [48.86, 2.35],     label: "Paris" },
          { id: "frankfurt", location: [50.11, 8.68],     label: "Frankfurt" },
          { id: "zurich",    location: [47.38, 8.54],     label: "Zurich" },
          { id: "amsterdam", location: [52.37, 4.90],     label: "Amsterdam" },
          { id: "dublin",    location: [53.35, -6.26],    label: "Dublin" },
          { id: "milan",     location: [45.46, 9.19],     label: "Milan" },
          { id: "madrid",    location: [40.42, -3.70],    label: "Madrid" },
          { id: "stockholm", location: [59.33, 18.07],    label: "Stockholm" },
          { id: "oslo",      location: [59.91, 10.75],    label: "Oslo" },
          { id: "copenhagen",location: [55.68, 12.57],    label: "Copenhagen" },

          { id: "singapore", location: [1.35, 103.82],    label: "Singapore" },
          { id: "seoul",     location: [37.57, 126.98],   label: "Seoul" },
          { id: "beijing",   location: [39.90, 116.40],   label: "Beijing" },
          { id: "shanghai",  location: [31.23, 121.47],   label: "Shanghai" },
          { id: "shenzhen",  location: [22.54, 114.06],   label: "Shenzhen" },
          { id: "taipei",    location: [25.03, 121.56],   label: "Taipei" },
          { id: "mumbai",    location: [19.08, 72.88],    label: "Mumbai" },
          { id: "bangalore", location: [12.97, 77.59],    label: "Bangalore" },
          { id: "delhi",     location: [28.61, 77.20],    label: "New Delhi" },

          { id: "dubai",     location: [25.20, 55.27],    label: "Dubai" },
          { id: "riyadh",    location: [24.71, 46.67],    label: "Riyadh" },
          { id: "telaviv",   location: [32.08, 34.78],    label: "Tel Aviv" },
          { id: "istanbul",  location: [41.01, 28.97],    label: "Istanbul" },

          { id: "sao_paulo", location: [-23.55, -46.63],  label: "São Paulo" },
          { id: "mexico",    location: [19.43, -99.13],   label: "Mexico City" },
          { id: "buenos",    location: [-34.60, -58.38],  label: "Buenos Aires" },

          { id: "johannesburg", location: [-26.20, 28.04], label: "Johannesburg" },
          { id: "cairo",         location: [30.04, 31.24], label: "Cairo" },
          { id: "lagos",         location: [6.52, 3.38],    label: "Lagos" },
          { id: "nairobi",       location: [-1.29, 36.82],  label: "Nairobi" },

          { id: "zurich_finance", location: [47.37, 8.54],  label: "Swiss Corporate HQs" },
          { id: "geneva",         location: [46.20, 6.14],  label: "Geneva Organizations" },
          { id: "brussels",       location: [50.85, 4.35],  label: "Brussels Corporate Affairs" },
          { id: "vienna",         location: [48.21, 16.37], label: "Vienna" },
          { id: "warsaw",         location: [52.23, 21.01], label: "Warsaw" },
          { id: "helsinki",       location: [60.17, 24.94], label: "Helsinki" },

          { id: "montreal",       location: [45.50, -73.57], label: "Montreal" },
          { id: "calgary",        location: [51.05, -114.07],label: "Calgary Energy HQs" },
          { id: "ottawa",         location: [45.42, -75.69], label: "Ottawa" },

          { id: "munich",         location: [48.14, 11.58],  label: "Munich Industrial HQs" },
          { id: "stuttgart",      location: [48.78, 9.18],   label: "Stuttgart Automotive HQs" },
          { id: "detroit",        location: [42.33, -83.05], label: "Detroit Automotive HQs" }
        ]}
      />

      <DataGrid
        title="Company Profile API: The 360-Degree Corporate View"
        subtitle="Unify company fundamentals, financial data, executive insights, web traffic, and supply chain relationships into a single company profile API. See the complete picture of any public company."
        bg={databg}
        stats={[
          { label: 'Public Companies', value: '100,000+' },
          { label: 'Executive Profiles', value: '500,000+' },
          { label: 'Web Traffic Metrics', value: 'Daily & Historical' },
        ]}
      />
      <CardList
        title="Profile, Predict, and Position with Company Intelligence"
        vid={'https://cdn.studio14a.com/axion/products/cards/corp.mp4'}
        points={[
          {
            "title": "Deep Corporate Action History",
            "details": "Access a meticulously cleaned timeline of dividends, splits, spinoffs, M&A, and name changes. Critical for adjusting historical price series and understanding capital allocation.",
            "cta": "Explore corporate actions",
            "url": "/docs"
          },
          {
            "title": "Executive & Boardroom Intelligence",
            "details": "Track management changes, insider trading filings, compensation structures, and board member networks. Gauge leadership stability and incentive alignment.",
            "cta": "Analyze leadership data",
            "url": "/docs"
          },
          {
            "title": "Digital Footprint & Brand Health",
            "details": "Quantify brand momentum with daily and historical web traffic, app download estimates, social media sentiment, and search trend data for non-financial growth signals.",
            "cta": "View digital metrics",
            "url": "/products/webtraffic"
          }
        ]}
      />

      <Responses
        heading="One Ticker, Unlimited Lenses Into the Market"
        subheading="See exactly what comes back from every Profile API endpoint, ready to wire into your app. Switch tabs to compare company fundamentals, valuation, analyst sentiment, and upcoming catalysts for the same stock."
        ticker="AAPL"
        tabs={[
          {
            title: 'Company Profile',
            description: 'Full corporate profile - leadership, governance risk scores, headquarters, and business summary.',
            data: {
              address1: "One Apple Park Way",
              city: "Cupertino",
              state: "CA",
              zip: "95014",
              country: "United States",
              website: "https://www.apple.com",
              industry: "Consumer Electronics",
              sector: "Technology",
              longBusinessSummary: "Apple Inc. designs, manufactures, and markets smartphones...",
              fullTimeEmployees: 166000,
              companyOfficers: [
                {
                  name: "Mr. Timothy D. Cook",
                  age: 63,
                  title: "CEO & Director",
                  totalPay: 16520856,
                },
              ],
              overallRisk: 1,
              governanceEpochDate: "2025-12-01T00:00:00.000Z",
              auditRisk: 7,
              boardRisk: 1,
              compensationRisk: 3,
              shareHolderRightsRisk: 1,
              compensationAsOfEpochDate: "2024-12-31T00:00:00.000Z",
              irWebsite: "http://investor.apple.com/",
              executiveTeam: [],
            },
          },
          {
            title: 'Performance Summary',
            description: 'Current trading stats, valuation multiples, dividend info, and 52-week range.',
            data: {
              previousClose: 271.84,
              open: 273.605,
              dayLow: 266.96,
              dayHigh: 273.62,
              volume: 51509672,
              averageVolume: 46958041,
              marketCap: 4039405731840,
              trailingPE: 36.49,
              forwardPE: 29.84,
              dividendRate: 1.04,
              dividendYield: 0.0038,
              exDividendDate: "2025-11-10T00:00:00.000Z",
              fiftyTwoWeekLow: 169.21,
              fiftyTwoWeekHigh: 288.62,
              beta: 1.107,
            },
          },
          {
            title: 'Recommendation Trends',
            description: 'Analyst consensus over the last four months - Strong Buy through Strong Sell.',
            data: [
              { period: "0m", strongBuy: 5, buy: 24, hold: 15, sell: 1, strongSell: 3 },
              { period: "-1m", strongBuy: 5, buy: 24, hold: 15, sell: 1, strongSell: 3 },
              { period: "-2m", strongBuy: 5, buy: 24, hold: 15, sell: 1, strongSell: 3 },
              { period: "-3m", strongBuy: 5, buy: 23, hold: 15, sell: 1, strongSell: 3 },
            ],
          },
          {
            title: 'Calendar Events',
            description: 'Upcoming earnings dates, earnings call dates, and dividend calendar with consensus estimates.',
            data: {
              earnings: {
                earningsDate: ["2026-01-29T21:00:00.000Z"],
                earningsCallDate: ["2025-10-30T21:00:00.000Z"],
                isEarningsDateEstimate: true,
                earningsAverage: 2.6647,
                earningsLow: 2.51,
                earningsHigh: 2.76,
                revenueAverage: 138253076640,
                revenueLow: 136679500000,
                revenueHigh: 142741000000,
              },
              exDividendDate: "2025-11-10T00:00:00.000Z",
              dividendDate: "2025-11-13T00:00:00.000Z",
            },
          },
          {
            title: 'Financial Metrics',
            description: 'A single-call composite of the latest fiscal year - revenue, income, balance sheet, cash flow, and derived ratios in camelCase.',
            data: {
              revenue: 143756000000,
              netIncome: 42097000000,
              totalAssets: 379297000000,
              totalLiabilities: 291107000000,
              stockholdersEquity: 88190000000,
              currentAssets: 158104000000,
              currentLiabilities: 162367000000,
              operatingCashFlow: 53925000000,
              capitalExpenditures: 2373000000,
              freeCashFlow: 51552000000,
              sharesOutstandingBasic: 14748158000,
              sharesOutstandingDiluted: 14810356000,
              currentRatio: 0.973744664864166,
              debtToAssets: 0.76749091081659
            },
          },
          {
            title: 'Financial Snapshot',
            description: 'Daily market snapshot - current price, analyst targets, margins, growth rates, and valuation ratios, updated daily.',
            data: {
              currentPrice: 272.19,
              targetHighPrice: 350,
              targetLowPrice: 215,
              targetMeanPrice: 287.70682,
              targetMedianPrice: 300,
              recommendationMean: 2,
              recommendationKey: 'buy',
              numberOfAnalystOpinions: 41,
              totalCash: 54697000960,
              totalCashPerShare: 3.702,
              ebitda: 144748003328,
              totalDebt: 112377004032,
              quickRatio: 0.771,
              currentRatio: 0.893,
              totalRevenue: 416161005568,
              debtToEquity: 152.411,
              revenuePerShare: 27.84,
              returnOnAssets: 0.22964,
              returnOnEquity: 1.7142199,
              grossProfits: 195201007616,
              freeCashflow: 78862254080,
              operatingCashflow: 111482003456,
              earningsGrowth: 0.912,
              revenueGrowth: 0.079,
              grossMargins: 0.46905,
              ebitdaMargins: 0.34782,
              operatingMargins: 0.31647,
              profitMargins: 0.26915002,
              financialCurrency: 'USD'
            },
          },
          {
            title: 'Key Statistics',
            description: 'Enterprise value, share float, short interest, ownership breakdown, and valuation ratios.',
            data: {
              enterpriseValue: 4079655583744,
              forwardPE: 29.84,
              profitMargins: 0.269,
              floatShares: 14750642146,
              sharesOutstanding: 14776353000,
              sharesShort: 129458559,
              shortRatio: 2.64,
              shortPercentOfFloat: 0.0088,
              heldPercentInsiders: 0.01697,
              heldPercentInstitutions: 0.64406,
              beta: 1.107,
              bookValue: 4.991,
              priceToBook: 54.54,
              lastSplitFactor: "4:1",
              lastSplitDate: 1598832000,
            },
          },
          {
            title: 'Company Info',
            description: 'A lighter-weight company snapshot - address, sector, industry, and headcount, without governance data.',
            data: {
              address1: "One Apple Park Way",
              city: "Cupertino",
              state: "CA",
              zip: "95014",
              country: "United States",
              phone: "(408) 996-1010",
              website: "https://www.apple.com",
              industry: "Consumer Electronics",
              sector: "Technology",
              longBusinessSummary: "Apple Inc. designs, manufactures, and markets smartphones, personal computers, tablets, wearables, and accessories worldwide...",
              fullTimeEmployees: 166000,
            },
          },
        ]}
      />

      <DatasetDetails full={true}
        title="Company Profile API: Comprehensive Company Intelligence"
        subtitle="Access detailed company fundamentals, financial data, executive leadership, board composition, business segments, and competitive analysis for over 100,000 public companies worldwide. Powers investment research, due diligence, competitive benchmarking, and alpha generation."
        cta="Get Access"
        ctaLink="/login"
        tiles={[
          {
            img: '13.webp',
            title: 'We profile 100,000+ companies',
            caption: 'You can access detailed company profiles with leadership, segments, financials, and competitive analysis.',
            cn: 'scaled',
            tile: <W1/>
          },
          {
            img: '5.webp',
            title: 'We track 500,000+ executives',
            caption: 'You can research C-suite and board-level company intelligence for companies worldwide.',
            cn: 'scaled',
            tile: <W2/>
          },
          {
            img: '10.webp',
            title: 'We provide 200+ data points per company',
            caption: 'You get a complete 360-degree view with company financial data, digital footprint, and relationship data.',
            cn: 'scaled',
            tile: <W3/>
          },
          {
            img: '12.webp',
            title: 'We update daily',
            caption: 'You always have the most current company fundamentals with daily data refreshes.',
            cn: 'scaled corner',
            tile: <W4/>
          },
          {
            img: '14.webp',
            title: 'We measure web traffic daily',
            caption: 'You can monitor digital footprint and competitive positioning with daily and historical web analytics.',
            cn: 'scaled',
            tile: <W5/>
          }
        ]}
      />
      
      <Outcomes
        headline="Company intelligence that speaks for itself."
        leftCell={{ stat: '100,000+', title: 'Public company profiles.', desc: 'Comprehensive company fundamentals coverage of global equities.', showChart: true }}
        rightCells={[
          { stat: '500,000+', title: 'Executive profiles.', desc: 'C-suite to board-level company information.' },
          { stat: '200+', title: 'Data points per company.', desc: 'Financial data, digital footprint, competition.' },
        ]}

      /><ProductFAQ slug="corporate-profiles" />


      <PricingTable />
      <GetStarted />

      <Footer />
      </Suspense>
      </StyledProducts>
    </>
  );
};

export default StocksPage;
